import { useTranslation } from 'react-i18next'
import { useContext } from 'react'
import { 
  Modal, 
  Text, 
  Group, 
  Stack, 
  Button, 
  Alert,
  Tabs,
  Divider,
  Box
} from 'components'
import { 
  IconSparkles, 
  IconCheck, 
  IconBook,
  IconTarget,
  IconRefresh
} from '@tabler/icons-react'
import { StepperContext } from '../../context/StepperContext'
import { useAISuggestionsModal } from './hooks/useAISuggestionsModal'
import { EmptyState } from './components/EmptyState'
import { TabContent } from './components/TabContent'

interface AISuggestionsModalV2Props {
  opened: boolean
  onClose: () => void
  projectId?: string | null
}

export function AISuggestionsModalV2({ opened, onClose, projectId }: AISuggestionsModalV2Props) {
  const { t } = useTranslation()
  const { setActive } = useContext(StepperContext)
  
  const {
    loading,
    projectItems,
    selectedIds,
    activeTab,
    totalItems,
    hasItems,
    availableItems,
    totalAvailableItems,
    allItemsAlreadyAdded,
    handleToggleItem,
    handleSelectAll,
    handleApplySuggestions,
    setActiveTab
  } = useAISuggestionsModal({ opened, onClose, projectId })

  const handleRegenerate = () => {
    // Usar flag específica para este fluxo (não interfere em outros comportamentos)
    localStorage.setItem('ai_regenerate_modal_flag', 'true')
    
    // Fechar modal atual
    onClose()
    
    // Ir para o Step 1 (index 0) - usa funcionalidade original do stepper
    setActive(0)
  }
  
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing='xs'>
          <IconSparkles size={24} style={{ color: '#8b5cf6' }} />
          <Text size='lg' weight={600}>{t('new_project_page.select_suggested_standards_skills')}</Text>
        </Group>
      }
      size='xl'
    >
      <Stack spacing='lg'>
        {!hasItems || totalItems === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Nova seção Want different suggestions? */}
            <Box
              sx={(theme) => ({
                backgroundColor: theme.colors.gray[0],
                borderRadius: theme.radius.md,
                padding: theme.spacing.md,
                border: `1px solid ${theme.colors.gray[3]}`,
              })}
            >
              <Group position="apart" align="center">
                <Text size="sm" weight={500} color='dimmed'>
                  {t('new_project_page.want_different_suggestions')}
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<IconRefresh size={16} />}
                  onClick={handleRegenerate}
                  color="grape"
                >
                  {t('new_project_page.regenerate')}
                </Button>
              </Group>
            </Box>

            <Alert 
              icon={<IconSparkles size={16} />} 
              color='grape' 
              variant='light'
            >
              <Text size='sm'>
                {totalItems > 0 
                  ? t('new_project_page.ai_suggestions_description_v2', { count: totalItems })
                  : 'Sugestões da IA encontradas'
                }
              </Text>
            </Alert>
            
            <Tabs value={activeTab} onTabChange={(value) => setActiveTab(value || 'contents')}>
              <Tabs.List>
                <Tabs.Tab 
                  value='contents' 
                  icon={<IconBook size={14} />}
                >
                  {t('new_project_page.contents')} ({availableItems.contents.length}/{projectItems?.contents?.length || 0})
                </Tabs.Tab>
                <Tabs.Tab 
                  value='skills' 
                  icon={<IconTarget size={14} />}
                >
                  {t('new_project_page.skills')} ({availableItems.skills.length}/{projectItems?.skills?.length || 0})
                </Tabs.Tab>
              </Tabs.List>
              
              <Tabs.Panel value='contents' pt='xs'>
                <TabContent
                  items={projectItems?.contents || []}
                  type='content'
                  selectedIds={selectedIds}
                  onToggleItem={handleToggleItem}
                  onSelectAll={() => handleSelectAll('contents')}
                />
              </Tabs.Panel>
              
              <Tabs.Panel value='skills' pt='xs'>
                <TabContent
                  items={projectItems?.skills || []}
                  type='skill'
                  selectedIds={selectedIds}
                  onToggleItem={handleToggleItem}
                  onSelectAll={() => handleSelectAll('skills')}
                />
              </Tabs.Panel>
            </Tabs>
            
            <Divider />
            
            <Group position='right' spacing='sm'>
              <Button
                variant='outline'
                onClick={onClose}
                size='md'
                disabled={loading}
              >
                {t('general_buttons_cancel')}
              </Button>
              <Button
                onClick={handleApplySuggestions}
                disabled={selectedIds.size === 0 || allItemsAlreadyAdded}
                loading={loading}
                leftIcon={!loading ? <IconCheck size={18} /> : undefined}
                color='grape'
                size='md'
              >
                {allItemsAlreadyAdded
                  ? 'Todos os itens já foram adicionados'
                  : t('new_project_page.add_items', { count: selectedIds.size })
                }
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}